import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, ScanLine, Trash2, X } from 'lucide-react';

import { BarcodeScannerDialog } from '@/components/quotes/BarcodeScannerDialog';
import { PlanLockBadge } from '@/components/quotes/PlanLockBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { pickCatalogName, pickLocalizedField } from '@/i18n/localizeItem';
import { addMonthsIso, splitSerials } from '@/lib/serials';
import { parseQty } from '@/lib/stockQty';
import { showApiError } from '@/lib/apiErrors';

const API_URL = process.env.REACT_APP_API_URL;
export const RECORD_TYPES = ['maintenance', 'repair', 'install', 'inspection'];
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function errorText(err, t) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return t('service.validation.form');
  return t('common.error');
}

/** Plain <select> of customers (keeps the dialog light on phones). */
export function CustomerSelect({ customers, value, onChange, disabled, testId }) {
  const { t } = useTranslation('quotes');
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-10 w-full rounded-md border border-[#E5E5E5] bg-white px-3 text-sm"
      data-testid={testId}
    >
      <option value="" disabled>{t('service.chooseCustomer')}</option>
      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

// ------------------------------------------------------------------ record
export function RecordDialog({ open, onClose, onSaved, customers, preset, canUseStock }) {
  const { t, i18n } = useTranslation('quotes');
  const lang = (i18n.language || 'tr').split('-')[0];
  const [form, setForm] = useState(null);
  const [devices, setDevices] = useState([]);
  const [stockItems, setStockItems] = useState(null);
  const [materialSearch, setMaterialSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      customer_id: preset?.customer_id || '',
      new_customer_name: '',
      type: preset?.type || 'maintenance',
      status: 'done',
      performed_at: todayIso(),
      description: '',
      result: '',
      device_ids: [],
      materials: [],
    });
    setMaterialSearch('');
  }, [open, preset]);

  useEffect(() => {
    if (!open || !form?.customer_id) { setDevices([]); return; }
    axios.get(`${API_URL}/api/quote/service/devices`, { params: { customer_id: form.customer_id } })
      .then(({ data }) => setDevices(data.items || []))
      .catch(() => setDevices([]));
  }, [open, form?.customer_id]);

  useEffect(() => {
    if (!open || !canUseStock || stockItems) return;
    axios.get(`${API_URL}/api/quote/stock`, { params: { scope: 'all', page_size: 1000 } })
      .then(({ data }) => setStockItems(data.items || []))
      .catch(() => setStockItems([]));
  }, [open, canUseStock, stockItems]);

  const matches = useMemo(() => {
    const q = materialSearch.trim().toLocaleLowerCase();
    if (!q || !stockItems) return [];
    const chosen = new Set((form?.materials || []).map((m) => m.item.id));
    return stockItems.filter((r) => !chosen.has(r.id) && [r.name, r.name_en, r.sku, r.brand, r.model]
      .some((v) => (v || '').toLocaleLowerCase().includes(q))).slice(0, 8);
  }, [materialSearch, stockItems, form?.materials]);

  if (!form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    const newName = form.new_customer_name.trim();
    if (!form.customer_id && !newName) { toast.error(t('service.record.customerRequired')); return; }
    const materials = [];
    for (const m of form.materials) {
      const qty = parseQty(m.qty);
      if (!qty || qty <= 0) { toast.error(t('stock.validation.qty')); return; }
      materials.push({ item_id: m.item.id, qty });
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/service/records`, {
        customer_id: form.customer_id || undefined,
        new_customer_name: form.customer_id ? undefined : newName,
        type: form.type,
        status: form.status,
        performed_at: form.performed_at || undefined,
        description: form.description || undefined,
        result: form.result || undefined,
        device_ids: form.device_ids,
        materials,
      });
      if (data.negative_items?.length) toast.warning(t('stock.quote.negativeToast', { names: data.negative_items.join(', ') }));
      else toast.success(t('common.saved'));
      if (data.customer?.created) toast.info(t('service.record.newCustomerCreated', { name: data.customer.name }));
      onSaved?.(data.record, data.customer);
      onClose();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('service.record.newTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('service.customer')}</Label>
            <CustomerSelect customers={customers} value={form.customer_id}
              onChange={(v) => set({ customer_id: v, new_customer_name: '', device_ids: [] })}
              disabled={!!preset?.customer_id} testId="record-customer" />
            {!preset?.customer_id && (
              <>
                <Input className="mt-2" value={form.new_customer_name} maxLength={200}
                  onChange={(e) => set({ new_customer_name: e.target.value, customer_id: e.target.value.trim() ? '' : form.customer_id, device_ids: [] })}
                  placeholder={t('service.record.newCustomerPlaceholder')} data-testid="record-new-customer" />
                {form.new_customer_name.trim() && (
                  <p className="mt-1 text-xs text-[#94A3B8]">{t('service.record.newCustomerHelp')}</p>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-4">
            {RECORD_TYPES.map((k) => (
              <button key={k} type="button" onClick={() => set({ type: k })}
                className={`rounded-lg py-2 text-sm font-medium ${form.type === k ? 'bg-white shadow text-[#1A1A2E]' : 'text-slate-500'}`}
                data-testid={`record-type-${k}`}>
                {t(`service.types.${k}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('service.record.date')}</Label>
              <Input type="date" value={form.performed_at} onChange={(e) => set({ performed_at: e.target.value })} />
            </div>
            <div>
              <Label>{t('service.record.status')}</Label>
              <select value={form.status} onChange={(e) => set({ status: e.target.value })}
                className="h-10 w-full rounded-md border border-[#E5E5E5] bg-white px-3 text-sm" data-testid="record-status">
                <option value="done">{t('service.status.done')}</option>
                <option value="open">{t('service.status.open')}</option>
              </select>
            </div>
          </div>
          <div>
            <Label>{t('service.record.description')}</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })}
              placeholder={t(`service.record.descriptionPlaceholder.${form.type}`)} data-testid="record-description" />
          </div>
          <div>
            <Label>{t('service.record.result')}</Label>
            <Textarea rows={2} value={form.result} onChange={(e) => set({ result: e.target.value })}
              placeholder={t('service.record.resultPlaceholder')} />
          </div>

          {devices.length > 0 && (
            <div>
              <Label>{t('service.record.devices')}</Label>
              <ul className="mt-1 max-h-40 divide-y divide-[#F1F5F9] overflow-y-auto rounded-xl border border-[#E5E5E5]">
                {devices.map((d) => (
                  <li key={d.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                      <Checkbox
                        checked={form.device_ids.includes(d.id)}
                        onCheckedChange={(v) => set({
                          device_ids: v ? [...form.device_ids, d.id] : form.device_ids.filter((x) => x !== d.id),
                        })}
                      />
                      <span className="min-w-0 flex-1 truncate">{d.name}{d.location ? ` · ${d.location}` : ''}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canUseStock && (
            <div>
              <Label>{t('service.record.materials')}</Label>
              {form.materials.length > 0 && (
                <ul className="mt-1 mb-2 divide-y divide-[#F1F5F9] rounded-xl border border-[#E5E5E5]">
                  {form.materials.map((m, idx) => (
                    <li key={m.item.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="min-w-0 flex-1 truncate">{pickCatalogName(m.item, lang)}</span>
                      <Input inputMode="decimal" className="h-8 w-20 text-right" value={m.qty}
                        onChange={(e) => set({ materials: form.materials.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)) })} />
                      <span className="w-10 text-xs text-[#94A3B8]">{pickLocalizedField(m.item, 'unit', lang)}</span>
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => set({ materials: form.materials.filter((_, i) => i !== idx) })}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <Input value={materialSearch} onChange={(e) => setMaterialSearch(e.target.value)}
                placeholder={t('service.record.materialSearch')} data-testid="record-material-search" />
              {matches.length > 0 && (
                <ul className="mt-1 divide-y divide-[#F1F5F9] rounded-xl border border-[#E5E5E5]">
                  {matches.map((r) => (
                    <li key={r.id}>
                      <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => { set({ materials: [...form.materials, { item: r, qty: '1' }] }); setMaterialSearch(''); }}>
                        <Plus className="h-4 w-4 text-[#2563EB]" />
                        <span className="min-w-0 flex-1 truncate">{pickCatalogName(r, lang)}</span>
                        <span className="text-xs text-[#94A3B8]">{r.stock_qty} {pickLocalizedField(r, 'unit', lang)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-xs text-[#94A3B8]">{t('service.record.materialsHelp')}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="record-save">{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ record detail
export function RecordDetailDialog({ record, onClose, onSaved }) {
  const { t, i18n } = useTranslation('quotes');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setForm({ description: record.description, result: record.result, performed_at: record.performed_at || '' });
  }, [record]);

  if (!record || !form) return null;

  const save = async (status) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/quote/service/records/${record.id}`, {
        ...form, ...(status ? { status } : {}),
      });
      toast.success(t('common.saved'));
      onSaved?.(data);
      onClose();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`service.types.${record.type}`)} · {record.customer_name}</DialogTitle>
          <DialogDescription>
            {t(`service.status.${record.status}`)}{record.user_name ? ` · ${record.user_name}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('service.record.date')}</Label>
            <Input type="date" value={form.performed_at} onChange={(e) => setForm({ ...form, performed_at: e.target.value })} />
          </div>
          <div>
            <Label>{t('service.record.description')}</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>{t('service.record.result')}</Label>
            <Textarea rows={3} value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} data-testid="record-detail-result" />
          </div>
          {record.device_names?.length > 0 && (
            <p className="text-sm text-[#475569]"><strong>{t('service.record.devices')}:</strong> {record.device_names.join(', ')}</p>
          )}
          {record.materials?.length > 0 && (
            <p className="text-sm text-[#475569]">
              <strong>{t('service.record.materials')}:</strong>{' '}
              {record.materials.map((m) => `${m.name} ${new Intl.NumberFormat(i18n.language || 'tr', { maximumFractionDigits: 3 }).format(m.qty)} ${m.unit}`).join(', ')}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          {record.status === 'open' ? (
            <Button variant="outline" onClick={() => save()} disabled={saving}>{t('common.save')}</Button>
          ) : (
            <Button variant="ghost" onClick={() => save('open')} disabled={saving}>{t('service.record.reopen')}</Button>
          )}
          {record.status === 'open' ? (
            <Button onClick={() => save('done')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-close">
              {t('service.record.markDone')}
            </Button>
          ) : (
            <Button onClick={() => save()} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]">{t('common.save')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ device
const DEFAULT_WARRANTY_MONTHS = 24;
const emptyDevice = () => ({
  name: '', brand: '', model: '', quantity: '1', serials: '', location: '',
  installed_at: todayIso(), warranty_until: '', notes: '',
});

const MAX_SERIALS_CHARS = 2000;

export function DeviceDialog({ open, onClose, onSaved, customerId, device }) {
  const { t } = useTranslation('quotes');
  const { entitlement } = useQuoteEntitlement();
  const scanLocked = entitlement?.limits?.barcode_scanning === false;
  const [form, setForm] = useState(emptyDevice());
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const serials = splitSerials(form.serials);
  const deviceCount = parseQty(form.quantity) || 0;
  const tooManySerials = serials.length > deviceCount;

  useEffect(() => {
    if (!open) return;
    setScanning(false);
    setDismissed([]);
    setForm(device ? {
      ...emptyDevice(), ...device,
      quantity: String(device.quantity ?? 1),
      installed_at: device.installed_at || '', warranty_until: device.warranty_until || '',
    } : emptyDevice());
  }, [open, device]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const warnIfUsed = async (serial) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/service/devices/by-serial`, { params: { q: serial, exact: true } });
      const other = (data.items || []).find((d) => d.id !== device?.id);
      if (other) toast.warning(t('service.serial.usedElsewhere', { serial, customer: other.customer_name }));
    } catch {
      // Only a warning: scanning carries on without it.
    }
  };

  const addSerial = (serial) => {
    setForm((f) => {
      const current = splitSerials(f.serials);
      if (current.includes(serial)) return f;
      const next = [...current, serial].join('\n');
      if (next.length > MAX_SERIALS_CHARS) return f;
      const warranty = f.warranty_until || !f.installed_at
        ? f.warranty_until : addMonthsIso(f.installed_at, DEFAULT_WARRANTY_MONTHS);
      return { ...f, serials: next, warranty_until: warranty };
    });
    if (serial.length >= 3) warnIfUsed(serial);
  };

  const removeSerial = (serial) => {
    setDismissed((list) => [...list, serial]);
    setForm((f) => ({ ...f, serials: splitSerials(f.serials).filter((s) => s !== serial).join('\n') }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('service.validation.deviceName')); return; }
    const quantity = parseQty(form.quantity);
    if (!quantity || quantity <= 0) { toast.error(t('stock.validation.qty')); return; }
    const payload = {
      name: form.name.trim(), brand: form.brand, model: form.model, quantity,
      serials: form.serials, location: form.location, notes: form.notes,
      installed_at: form.installed_at || null, warranty_until: form.warranty_until || null,
    };
    setSaving(true);
    try {
      if (device) await axios.put(`${API_URL}/api/quote/service/devices/${device.id}`, payload);
      else await axios.post(`${API_URL}/api/quote/service/devices`, { ...payload, customer_id: customerId });
      toast.success(t('common.saved'));
      onSaved?.();
      onClose();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <BarcodeScannerDialog
      open={open && scanning}
      mode="serial"
      continuous
      known={[...serials, ...dismissed]}
      title={t('service.serial.scan')}
      help={t('service.serial.scanHelp')}
      onClose={() => setScanning(false)}
      onDetected={addSerial}
    >
      <div>
        <p className="mb-1 text-sm font-medium text-[#1A1A2E]" data-testid="serial-scan-count">
          {t('service.serial.count', { count: serials.length, quantity: deviceCount })}
        </p>
        {tooManySerials && <p className="mb-1 text-sm text-amber-700">{t('service.serial.tooMany')}</p>}
        <ul className="max-h-40 divide-y divide-[#F1F5F9] overflow-y-auto">
          {[...serials].reverse().map((serial) => (
            <li key={serial} className="flex items-center justify-between gap-2 py-1 text-sm">
              <span className="truncate">{serial}</span>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeSerial(serial)} aria-label={t('service.serial.remove')}>
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
        <Button type="button" className="mt-2 w-full bg-[#2563EB] hover:bg-[#1E4ED8]" onClick={() => setScanning(false)} data-testid="serial-scan-done">
          {t('service.serial.done')}
        </Button>
      </div>
    </BarcodeScannerDialog>
    <Dialog open={open && !scanning} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{device ? t('service.device.editTitle') : t('service.device.newTitle')}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>{t('service.device.name')}</Label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="device-name" />
          </div>
          <div><Label>{t('service.device.brand')}</Label><Input value={form.brand} onChange={(e) => set({ brand: e.target.value })} /></div>
          <div><Label>{t('service.device.model')}</Label><Input value={form.model} onChange={(e) => set({ model: e.target.value })} /></div>
          <div><Label>{t('service.device.quantity')}</Label><Input inputMode="decimal" value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} /></div>
          <div><Label>{t('service.device.location')}</Label><Input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder={t('service.device.locationPlaceholder')} /></div>
          <div><Label>{t('service.device.installedAt')}</Label><Input type="date" value={form.installed_at} onChange={(e) => set({ installed_at: e.target.value })} /></div>
          <div><Label>{t('service.device.warrantyUntil')}</Label><Input type="date" value={form.warranty_until} onChange={(e) => set({ warranty_until: e.target.value })} /></div>
          <div className="col-span-2">
            <div className="mb-1 flex items-center justify-between gap-2">
              <Label>{t('service.device.serials')}</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => (scanLocked ? window.location.assign('/quote-upgrade') : setScanning(true))}
                data-testid="device-scan-serial"
              >
                <ScanLine className="mr-1.5 h-4 w-4" /> {t('service.serial.scan')}
                {scanLocked && <PlanLockBadge />}
              </Button>
            </div>
            <Textarea rows={2} maxLength={MAX_SERIALS_CHARS} value={form.serials} onChange={(e) => set({ serials: e.target.value })} placeholder={t('service.device.serialsPlaceholder')} data-testid="device-serials" />
            {tooManySerials && <p className="mt-1 text-sm text-amber-700" data-testid="device-serials-warning">{t('service.serial.tooMany')}</p>}
          </div>
          <div className="col-span-2">
            <Label>{t('service.device.notes')}</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="device-save">{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ------------------------------------------------------------------ plan
export function PlanDialog({ open, onClose, onSaved, customerId, plan }) {
  const { t } = useTranslation('quotes');
  const [intervalMonths, setIntervalMonths] = useState('6');
  const [nextDue, setNextDue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIntervalMonths(String(plan?.interval_months || 6));
    setNextDue(plan?.next_due_at || todayIso());
    setNotes(plan?.notes || '');
  }, [open, plan]);

  const save = async () => {
    const months = parseInt(intervalMonths, 10);
    if (!months || months < 1 || months > 60 || !nextDue) { toast.error(t('service.validation.plan')); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/quote/service/plans/${customerId}`, { interval_months: months, next_due_at: nextDue, notes });
      toast.success(t('common.saved'));
      onSaved?.();
      onClose();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('service.plan.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/service/plans/${customerId}`);
      onSaved?.();
      onClose();
    } catch (err) {
      showApiError(err, t);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('service.plan.title')}</DialogTitle>
          <DialogDescription>{t('service.plan.help')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{t('service.plan.interval')}</Label>
            <select value={intervalMonths} onChange={(e) => setIntervalMonths(e.target.value)}
              className="h-10 w-full rounded-md border border-[#E5E5E5] bg-white px-3 text-sm" data-testid="plan-interval">
              {[1, 2, 3, 4, 6, 12, 24].map((m) => <option key={m} value={m}>{t('service.plan.everyMonths', { count: m })}</option>)}
            </select>
          </div>
          <div>
            <Label>{t('service.plan.nextDue')}</Label>
            <Input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>{t('service.plan.notes')}</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('service.plan.notesPlaceholder')} />
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          {plan ? <Button variant="ghost" className="text-red-600" onClick={remove}>{t('service.plan.delete')}</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="plan-save">{t('common.save')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
